// Assembly Debugger Challenge - Quiz Engine
class AssemblyQuizEngine {
    constructor() {
        this.currentQuiz = null;
        this.currentQuestion = 0;
        this.score = 0;
        this.timeLimit = 0;
        this.timeRemaining = 0;
        this.timer = null;
        this.answers = [];
        this.startTime = null;
        this.cpuState = this.initializeCPUState();
        this.achievements = new Set();
        this.stats = this.loadStats();
        this.streakCount = 0;
        
        this.initializeEventListeners();
        this.loadPlayerStats();
        this.initializeQuestionBank();
    }

    initializeCPUState() {
        return {
            rax: 0, rbx: 0, rcx: 0, rdx: 0,
            rsp: 0x7fff12345678, rbp: 0x7fff12345680,
            flags: { zf: 0, cf: 0, sf: 0, of: 0 }
        };
    }

    initializeQuestionBank() {
        this.questionBank = {
            quick: [
                {
                    type: 'multiple-choice',
                    title: 'Register Knowledge',
                    question: 'What is the primary purpose of the RAX register in x86-64?',
                    code: null,
                    options: [
                        'Stack pointer management',
                        'Accumulator for arithmetic operations and return values',
                        'Base pointer for memory addressing',
                        'Counter for loop operations'
                    ],
                    correct: 1,
                    explanation: 'RAX is the accumulator register, primarily used for arithmetic operations and storing function return values.',
                    topic: 'registers'
                },
                {
                    type: 'multiple-choice',
                    title: 'Instruction Identification',
                    question: 'What does the MOV instruction do?',
                    code: 'mov rax, rbx',
                    options: [
                        'Adds RBX to RAX',
                        'Copies the value from RBX to RAX',
                        'Compares RAX and RBX',
                        'Exchanges values between RAX and RBX'
                    ],
                    correct: 1,
                    explanation: 'MOV copies data from the source (RBX) to the destination (RAX). The source value remains unchanged.',
                    topic: 'data-movement'
                },
                {
                    type: 'code-analysis',
                    title: 'Register Size Understanding',
                    question: 'After executing this instruction, what happens to the upper 32 bits of RAX?',
                    code: 'mov eax, 0x12345678',
                    options: [
                        'They remain unchanged',
                        'They are set to 0x12345678',
                        'They are zeroed out (cleared)',
                        'They are set to 0xFFFFFFFF'
                    ],
                    correct: 2,
                    explanation: 'When writing to a 32-bit register (EAX), the upper 32 bits of the corresponding 64-bit register (RAX) are automatically zeroed.',
                    topic: 'registers'
                },
                {
                    type: 'multiple-choice',
                    title: 'Assembly vs Machine Code',
                    question: 'What is the relationship between assembly language and machine code?',
                    code: null,
                    options: [
                        'Assembly language is compiled to machine code',
                        'Assembly language is assembled to machine code',
                        'Assembly language and machine code are the same',
                        'Assembly language interprets machine code'
                    ],
                    correct: 1,
                    explanation: 'Assembly language is assembled (not compiled) to machine code by an assembler. Each assembly instruction corresponds to a specific machine code pattern.',
                    topic: 'fundamentals'
                },
                {
                    type: 'code-analysis',
                    title: 'Stack Operations',
                    question: 'What is the final value in RAX after these instructions?',
                    code: `mov rax, 10
push rax
mov rax, 20
pop rbx`,
                    options: ['10', '20', '30', '0'],
                    correct: 1,
                    explanation: 'RAX is set to 10, then pushed onto stack. RAX is changed to 20. POP puts the stack value (10) into RBX, not RAX. So RAX remains 20.',
                    topic: 'data-movement'
                }
            ],
            tracer: [
                {
                    type: 'cpu-trace',
                    title: 'Register State Prediction',
                    question: 'Step through this code and predict the final register states:',
                    code: `mov rax, 5
mov rbx, 3
add rax, rbx
mov rcx, rax`,
                    steps: [
                        { instruction: 'mov rax, 5', state: { rax: 5 } },
                        { instruction: 'mov rbx, 3', state: { rbx: 3 } },
                        { instruction: 'add rax, rbx', state: { rax: 8 } },
                        { instruction: 'mov rcx, rax', state: { rcx: 8 } }
                    ],
                    question_after: 'What is the final value in RCX?',
                    options: ['3', '5', '8', '15'],
                    correct: 2,
                    explanation: 'RAX = 5, RBX = 3, RAX = RAX + RBX = 8, RCX = RAX = 8',
                    topic: 'arithmetic'
                },
                {
                    type: 'cpu-trace',
                    title: 'Memory and Register Interaction',
                    question: 'Trace through this memory operation:',
                    code: `mov rax, 100
mov [rbp-8], rax
mov rbx, 50
add [rbp-8], rbx
mov rcx, [rbp-8]`,
                    steps: [
                        { instruction: 'mov rax, 100', state: { rax: 100 } },
                        { instruction: 'mov [rbp-8], rax', state: { memory: { 'rbp-8': 100 } } },
                        { instruction: 'mov rbx, 50', state: { rbx: 50 } },
                        { instruction: 'add [rbp-8], rbx', state: { memory: { 'rbp-8': 150 } } },
                        { instruction: 'mov rcx, [rbp-8]', state: { rcx: 150 } }
                    ],
                    question_after: 'What is the final value in RCX?',
                    options: ['50', '100', '150', '200'],
                    correct: 2,
                    explanation: 'The memory location [rbp-8] starts with 100, then 50 is added to it (making it 150), and this value is loaded into RCX.',
                    topic: 'memory'
                },
                {
                    type: 'flag-prediction',
                    title: 'Flag State Analysis',
                    question: 'After this comparison, which flags will be set?',
                    code: `mov rax, 5
mov rbx, 10
cmp rax, rbx`,
                    options: [
                        'ZF=1, CF=0, SF=0',
                        'ZF=0, CF=1, SF=1',
                        'ZF=0, CF=0, SF=0',
                        'ZF=1, CF=1, SF=0'
                    ],
                    correct: 1,
                    explanation: 'CMP subtracts RBX from RAX (5-10=-5). Result is negative (SF=1), not zero (ZF=0), and there was a borrow (CF=1).',
                    topic: 'flags'
                }
            ],
            debugger: [
                {
                    type: 'bug-hunt',
                    title: 'Syntax Error Detection',
                    question: 'Find the syntax error in this assembly code:',
                    code: `mov rax, 10
add rax rbx
mov rbx, rax`,
                    options: [
                        'Line 1: Invalid immediate value',
                        'Line 2: Missing comma between operands',
                        'Line 3: Invalid register name',
                        'No errors present'
                    ],
                    correct: 1,
                    explanation: 'Line 2 is missing a comma between the destination (rax) and source (rbx) operands. Should be: add rax, rbx',
                    topic: 'syntax'
                },
                {
                    type: 'logic-error',
                    title: 'Logic Error in Loop',
                    question: 'This code is supposed to count from 1 to 5, but has a logic error. What\'s wrong?',
                    code: `mov rcx, 1
loop_start:
    ; do something
    add rcx, 1
    cmp rcx, 5
    jle loop_start`,
                    options: [
                        'Should use JL instead of JLE',
                        'RCX should start at 0',
                        'Should compare with 6, not 5',
                        'ADD instruction should be SUB'
                    ],
                    correct: 2,
                    explanation: 'The loop runs while RCX ≤ 5, so it will execute with RCX = 1,2,3,4,5,6. To count 1-5, compare with 6 or use JL instead of JLE.',
                    topic: 'control-flow'
                },
                {
                    type: 'memory-error',
                    title: 'Stack Imbalance',
                    question: 'What\'s wrong with this function\'s stack management?',
                    code: `my_function:
    push rbp
    mov rbp, rsp
    push rbx
    ; function body
    pop rbx
    mov rsp, rbp
    ret`,
                    options: [
                        'Missing function prologue',
                        'RBX is not callee-saved',
                        'Missing POP RBP before return',
                        'RSP restoration is incorrect'
                    ],
                    correct: 2,
                    explanation: 'The function pushes RBP but never pops it back. Should have "pop rbp" before "ret" to restore the caller\'s frame pointer.',
                    topic: 'functions'
                }
            ],
            mastery: [
                // Combination of all question types with increasing difficulty
                {
                    type: 'multiple-choice',
                    title: 'Advanced Register Usage',
                    question: 'In the System V ABI calling convention, which register is used for the first integer argument?',
                    code: null,
                    options: ['RAX', 'RDI', 'RSI', 'RCX'],
                    correct: 1,
                    explanation: 'In System V ABI (used on Linux), RDI holds the first integer argument, RSI the second, RDX the third, etc.',
                    topic: 'calling-conventions'
                },
                {
                    type: 'complex-trace',
                    title: 'Function Call Analysis',
                    question: 'Trace this function call and predict the return value:',
                    code: `mov rdi, 7
call factorial
; What is in RAX?

factorial:
    cmp rdi, 1
    jle base_case
    push rdi
    dec rdi
    call factorial
    pop rdi
    mul rdi
    ret
base_case:
    mov rax, 1
    ret`,
                    options: ['1', '7', '5040', 'Stack overflow'],
                    correct: 2,
                    explanation: 'This calculates 7! = 7 × 6 × 5 × 4 × 3 × 2 × 1 = 5040. Note: This code has a bug (should use IMUL), but with small numbers it works.',
                    topic: 'functions'
                }
            ]
        };
    }

    loadStats() {
        const saved = localStorage.getItem('assemblyQuizStats');
        return saved ? JSON.parse(saved) : {
            totalScore: 0,
            testsCompleted: 0,
            bestStreak: 0,
            achievementCount: 0,
            topicScores: {}
        };
    }

    saveStats() {
        localStorage.setItem('assemblyQuizStats', JSON.stringify(this.stats));
    }

    loadPlayerStats() {
        document.getElementById('total-score').textContent = this.stats.totalScore;
        document.getElementById('tests-completed').textContent = this.stats.testsCompleted;
        document.getElementById('best-streak').textContent = this.stats.bestStreak;
        document.getElementById('achievements').textContent = this.stats.achievementCount;
    }

    initializeEventListeners() {
        // Quiz mode selection
        document.querySelectorAll('.quiz-mode-card').forEach(card => {
            card.addEventListener('click', () => this.startQuiz(card.dataset.mode));
        });

        // Answer selection
        document.getElementById('submit-answer').addEventListener('click', () => this.submitAnswer());
        document.getElementById('skip-question').addEventListener('click', () => this.skipQuestion());
        document.getElementById('next-question').addEventListener('click', () => this.nextQuestion());

        // CPU Simulator controls
        document.getElementById('step-button').addEventListener('click', () => this.stepCPU());
        document.getElementById('reset-button').addEventListener('click', () => this.resetCPU());

        // Results screen actions
        document.getElementById('retake-quiz').addEventListener('click', () => this.retakeQuiz());
        document.getElementById('try-different').addEventListener('click', () => this.showQuizMenu());
        document.getElementById('review-material').addEventListener('click', () => this.reviewMaterial());
        document.getElementById('share-results').addEventListener('click', () => this.shareResults());
    }

    startQuiz(mode) {
        this.currentQuiz = {
            mode: mode,
            questions: this.shuffleArray([...this.questionBank[mode]]).slice(0, this.getQuestionCount(mode)),
            timeLimit: this.getTimeLimit(mode)
        };
        
        this.currentQuestion = 0;
        this.score = 0;
        this.answers = [];
        this.streakCount = 0;
        this.startTime = Date.now();
        this.timeRemaining = this.currentQuiz.timeLimit;

        this.showScreen('quiz-game');
        this.startTimer();
        this.displayQuestion();
    }

    getQuestionCount(mode) {
        const counts = { quick: 5, tracer: 8, debugger: 6, mastery: 15 };
        return counts[mode] || 5;
    }

    getTimeLimit(mode) {
        const limits = { quick: 120, tracer: 300, debugger: 240, mastery: 600 }; // seconds
        return limits[mode] || 120;
    }

    startTimer() {
        this.timer = setInterval(() => {
            this.timeRemaining--;
            this.updateTimerDisplay();
            
            if (this.timeRemaining <= 0) {
                clearInterval(this.timer);
                this.timeUp();
            }
        }, 1000);
    }

    updateTimerDisplay() {
        const minutes = Math.floor(this.timeRemaining / 60);
        const seconds = this.timeRemaining % 60;
        document.getElementById('timer').textContent = `⏱️ ${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    displayQuestion() {
        const question = this.currentQuiz.questions[this.currentQuestion];
        
        // Update progress
        const progress = ((this.currentQuestion + 1) / this.currentQuiz.questions.length) * 100;
        document.getElementById('progress-fill').style.width = `${progress}%`;
        document.getElementById('progress-text').textContent = 
            `Question ${this.currentQuestion + 1} of ${this.currentQuiz.questions.length}`;
        
        // Update score
        document.getElementById('current-score').textContent = `Score: ${this.score}`;

        // Display question content
        document.getElementById('question-title').textContent = question.title;
        document.getElementById('question-type').textContent = this.getQuestionTypeDisplay(question.type);
        document.getElementById('question-text').textContent = question.question;

        // Show/hide code display
        const codeDisplay = document.getElementById('code-display');
        if (question.code) {
            codeDisplay.style.display = 'block';
            document.getElementById('code-content').textContent = question.code;
            this.highlightAssemblyCode(document.getElementById('code-content'));
        } else {
            codeDisplay.style.display = 'none';
        }

        // Show/hide CPU simulator
        const simulatorPanel = document.getElementById('simulator-panel');
        if (question.type === 'cpu-trace' || question.type === 'flag-prediction') {
            simulatorPanel.style.display = 'block';
            this.resetCPU();
        } else {
            simulatorPanel.style.display = 'none';
        }

        // Generate answer options
        this.generateAnswerOptions(question);
        
        // Reset feedback
        document.getElementById('quiz-feedback').style.display = 'none';
    }

    getQuestionTypeDisplay(type) {
        const displays = {
            'multiple-choice': 'Multiple Choice',
            'code-analysis': 'Code Analysis',
            'cpu-trace': 'CPU Tracer',
            'flag-prediction': 'Flag Prediction',
            'bug-hunt': 'Bug Hunter',
            'logic-error': 'Logic Error',
            'memory-error': 'Memory Error',
            'complex-trace': 'Advanced Trace'
        };
        return displays[type] || 'Question';
    }

    generateAnswerOptions(question) {
        const optionsContainer = document.getElementById('answer-options');
        optionsContainer.innerHTML = '';

        question.options.forEach((option, index) => {
            const optionElement = document.createElement('div');
            optionElement.className = 'answer-option';
            optionElement.innerHTML = `
                <input type="radio" name="answer" value="${index}" id="option-${index}">
                <label for="option-${index}">${option}</label>
            `;
            optionsContainer.appendChild(optionElement);
        });

        // Add event listeners
        document.querySelectorAll('input[name="answer"]').forEach(input => {
            input.addEventListener('change', () => {
                document.getElementById('submit-answer').disabled = false;
            });
        });

        document.getElementById('submit-answer').disabled = true;
    }

    highlightAssemblyCode(element) {
        let content = element.textContent;
        
        // Assembly keywords
        content = content.replace(/\b(mov|add|sub|mul|div|push|pop|call|ret|jmp|je|jne|jl|jle|jg|jge|cmp|test|lea|inc|dec)\b/gi, 
            '<span class="keyword">$1</span>');
        
        // Registers
        content = content.replace(/\b(rax|rbx|rcx|rdx|rsi|rdi|rsp|rbp|r8|r9|r10|r11|r12|r13|r14|r15|eax|ebx|ecx|edx)\b/gi, 
            '<span class="register">$1</span>');
        
        // Numbers
        content = content.replace(/\b(0x[0-9a-fA-F]+|\d+)\b/g, '<span class="number">$1</span>');
        
        // Comments
        content = content.replace(/(;.*$)/gm, '<span class="comment">$1</span>');
        
        // Labels
        content = content.replace(/^(\w+):/gm, '<span class="label">$1:</span>');
        
        element.innerHTML = content;
    }

    stepCPU() {
        const question = this.currentQuiz.questions[this.currentQuestion];
        if (question.type === 'cpu-trace' && question.steps) {
            // Implementation for stepping through CPU state
            // This would update the register display based on the current step
            this.updateCPUDisplay();
        }
    }

    resetCPU() {
        this.cpuState = this.initializeCPUState();
        this.updateCPUDisplay();
    }

    updateCPUDisplay() {
        document.getElementById('reg-rax').textContent = `0x${this.cpuState.rax.toString(16).padStart(16, '0')}`;
        document.getElementById('reg-rbx').textContent = `0x${this.cpuState.rbx.toString(16).padStart(16, '0')}`;
        document.getElementById('reg-rcx').textContent = `0x${this.cpuState.rcx.toString(16).padStart(16, '0')}`;
        document.getElementById('reg-rdx').textContent = `0x${this.cpuState.rdx.toString(16).padStart(16, '0')}`;
        document.getElementById('reg-rsp').textContent = `0x${this.cpuState.rsp.toString(16)}`;
        document.getElementById('reg-rbp').textContent = `0x${this.cpuState.rbp.toString(16)}`;
        document.getElementById('reg-flags').textContent = 
            `ZF=${this.cpuState.flags.zf} CF=${this.cpuState.flags.cf} SF=${this.cpuState.flags.sf}`;
    }

    submitAnswer() {
        const selectedAnswer = document.querySelector('input[name="answer"]:checked');
        if (!selectedAnswer) return;

        const question = this.currentQuiz.questions[this.currentQuestion];
        const answerIndex = parseInt(selectedAnswer.value);
        const isCorrect = answerIndex === question.correct;

        this.answers.push({
            questionIndex: this.currentQuestion,
            selectedAnswer: answerIndex,
            correct: isCorrect,
            topic: question.topic
        });

        if (isCorrect) {
            this.score += this.calculateQuestionScore();
            this.streakCount++;
        } else {
            this.streakCount = 0;
        }

        this.showFeedback(isCorrect, question.explanation);
    }

    calculateQuestionScore() {
        const baseScore = 10;
        const timeBonus = Math.max(0, Math.floor(this.timeRemaining / 10));
        const streakBonus = Math.min(this.streakCount, 5) * 2;
        return baseScore + timeBonus + streakBonus;
    }

    showFeedback(isCorrect, explanation) {
        const feedback = document.getElementById('quiz-feedback');
        const icon = document.getElementById('feedback-icon');
        const text = document.getElementById('feedback-text');
        const explanationEl = document.getElementById('feedback-explanation');

        icon.textContent = isCorrect ? '✅' : '❌';
        text.textContent = isCorrect ? 'Correct!' : 'Incorrect';
        explanationEl.textContent = explanation;

        feedback.style.display = 'block';
    }

    skipQuestion() {
        this.answers.push({
            questionIndex: this.currentQuestion,
            selectedAnswer: -1,
            correct: false,
            topic: this.currentQuiz.questions[this.currentQuestion].topic
        });
        this.streakCount = 0;
        this.nextQuestion();
    }

    nextQuestion() {
        this.currentQuestion++;
        
        if (this.currentQuestion >= this.currentQuiz.questions.length) {
            this.endQuiz();
        } else {
            this.displayQuestion();
        }
    }

    endQuiz() {
        clearInterval(this.timer);
        this.calculateFinalScore();
        this.checkAchievements();
        this.updateStats();
        this.showResults();
    }

    calculateFinalScore() {
        const totalPossible = this.currentQuiz.questions.length * 10;
        const timeBonus = Math.max(0, Math.floor(this.timeRemaining / 5));
        this.finalScore = Math.min(100, Math.floor((this.score / totalPossible) * 100) + timeBonus);
    }

    checkAchievements() {
        const correctAnswers = this.answers.filter(a => a.correct).length;
        const accuracy = correctAnswers / this.answers.length;
        
        // Perfect Score
        if (accuracy === 1.0) {
            this.addAchievement('perfect', 'Perfect Score!', 'Answered all questions correctly');
        }
        
        // Speed Demon
        if (this.timeRemaining > this.currentQuiz.timeLimit * 0.5) {
            this.addAchievement('speed', 'Speed Demon', 'Completed quiz in under half the time limit');
        }
        
        // First Quiz
        if (this.stats.testsCompleted === 0) {
            this.addAchievement('first', 'First Steps', 'Completed your first quiz');
        }
        
        // High Streak
        if (this.streakCount >= 5) {
            this.addAchievement('streak', 'On Fire!', 'Answered 5 questions correctly in a row');
        }
    }

    addAchievement(id, name, description) {
        if (!this.achievements.has(id)) {
            this.achievements.add(id);
            this.stats.achievementCount++;
            
            // Show achievement notification
            this.showAchievementNotification(name, description);
        }
    }

    showAchievementNotification(name, description) {
        // Create a temporary notification
        const notification = document.createElement('div');
        notification.className = 'achievement-notification';
        notification.innerHTML = `
            <div class="achievement-icon">🏆</div>
            <div class="achievement-content">
                <div class="achievement-name">${name}</div>
                <div class="achievement-description">${description}</div>
            </div>
        `;
        
        document.body.appendChild(notification);
        
        setTimeout(() => {
            notification.remove();
        }, 3000);
    }

    updateStats() {
        this.stats.totalScore += this.score;
        this.stats.testsCompleted++;
        this.stats.bestStreak = Math.max(this.stats.bestStreak, this.streakCount);
        
        // Update topic performance
        this.answers.forEach(answer => {
            if (!this.stats.topicScores[answer.topic]) {
                this.stats.topicScores[answer.topic] = { correct: 0, total: 0 };
            }
            this.stats.topicScores[answer.topic].total++;
            if (answer.correct) {
                this.stats.topicScores[answer.topic].correct++;
            }
        });
        
        this.saveStats();
    }

    showResults() {
        this.showScreen('quiz-results');
        
        const correctCount = this.answers.filter(a => a.correct).length;
        const totalQuestions = this.answers.length;
        const accuracy = Math.round((correctCount / totalQuestions) * 100);
        const timeTaken = Math.floor((Date.now() - this.startTime) / 1000);
        const timeBonus = Math.max(0, Math.floor(this.timeRemaining / 5));
        
        // Update results display
        document.getElementById('final-score').textContent = `Score: ${this.finalScore}/100`;
        document.getElementById('correct-count').textContent = `${correctCount}/${totalQuestions}`;
        document.getElementById('accuracy').textContent = `${accuracy}%`;
        document.getElementById('time-taken').textContent = this.formatTime(timeTaken);
        document.getElementById('time-bonus').textContent = `+${timeBonus} pts`;
        
        // Set results icon and title based on performance
        const resultsIcon = document.getElementById('results-icon');
        const resultsTitle = document.getElementById('results-title');
        
        if (this.finalScore >= 90) {
            resultsIcon.textContent = '🏆';
            resultsTitle.textContent = 'Excellent Work!';
        } else if (this.finalScore >= 70) {
            resultsIcon.textContent = '🎯';
            resultsTitle.textContent = 'Good Job!';
        } else if (this.finalScore >= 50) {
            resultsIcon.textContent = '📈';
            resultsTitle.textContent = 'Keep Practicing!';
        } else {
            resultsIcon.textContent = '📚';
            resultsTitle.textContent = 'Review and Try Again';
        }
        
        // Show topic performance
        this.displayTopicPerformance();
        this.displayEarnedAchievements();
    }

    displayTopicPerformance() {
        const chart = document.getElementById('analysis-chart');
        chart.innerHTML = '';
        
        const topicCounts = {};
        this.answers.forEach(answer => {
            if (!topicCounts[answer.topic]) {
                topicCounts[answer.topic] = { correct: 0, total: 0 };
            }
            topicCounts[answer.topic].total++;
            if (answer.correct) {
                topicCounts[answer.topic].correct++;
            }
        });
        
        Object.entries(topicCounts).forEach(([topic, counts]) => {
            const percentage = Math.round((counts.correct / counts.total) * 100);
            const topicElement = document.createElement('div');
            topicElement.className = 'topic-performance';
            topicElement.innerHTML = `
                <span class="topic">${this.formatTopicName(topic)}</span>
                <div class="performance-bar">
                    <div class="performance-fill" style="width: ${percentage}%"></div>
                </div>
                <span class="percentage">${percentage}%</span>
            `;
            chart.appendChild(topicElement);
        });
    }

    displayEarnedAchievements() {
        const achievementList = document.getElementById('achievement-list');
        achievementList.innerHTML = '';
        
        if (this.achievements.size === 0) {
            achievementList.innerHTML = '<p>No new achievements this time. Keep trying!</p>';
        } else {
            this.achievements.forEach(achievementId => {
                const achievement = this.getAchievementDetails(achievementId);
                const achievementElement = document.createElement('div');
                achievementElement.className = 'achievement-item';
                achievementElement.innerHTML = `
                    <span class="achievement-badge">🏅</span>
                    <span class="achievement-text">${achievement.name}</span>
                `;
                achievementList.appendChild(achievementElement);
            });
        }
    }

    getAchievementDetails(id) {
        const achievements = {
            perfect: { name: 'Perfect Score!' },
            speed: { name: 'Speed Demon' },
            first: { name: 'First Steps' },
            streak: { name: 'On Fire!' }
        };
        return achievements[id] || { name: 'Achievement' };
    }

    formatTopicName(topic) {
        const names = {
            'registers': 'Registers',
            'data-movement': 'Data Movement',
            'arithmetic': 'Arithmetic',
            'memory': 'Memory',
            'flags': 'Flags',
            'syntax': 'Syntax',
            'control-flow': 'Control Flow',
            'functions': 'Functions',
            'fundamentals': 'Fundamentals',
            'calling-conventions': 'Calling Conventions'
        };
        return names[topic] || topic;
    }

    formatTime(seconds) {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    retakeQuiz() {
        this.startQuiz(this.currentQuiz.mode);
    }

    showQuizMenu() {
        this.showScreen('quiz-menu');
        this.loadPlayerStats();
    }

    reviewMaterial() {
        window.location.href = 'sections/section1-fundamentals.html';
    }

    shareResults() {
        const shareText = `I just scored ${this.finalScore}/100 on The Assembly Debugger Challenge! 🔧 Can you beat my score?`;
        
        if (navigator.share) {
            navigator.share({
                title: 'Assembly Debugger Challenge',
                text: shareText,
                url: window.location.href
            });
        } else {
            // Fallback: copy to clipboard
            navigator.clipboard.writeText(shareText).then(() => {
                alert('Results copied to clipboard!');
            });
        }
    }

    timeUp() {
        alert('Time\'s up!');
        this.endQuiz();
    }

    showScreen(screenId) {
        document.querySelectorAll('.quiz-screen').forEach(screen => {
            screen.classList.remove('active');
        });
        document.getElementById(screenId).classList.add('active');
    }

    shuffleArray(array) {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }
}

// Initialize the quiz engine when the page loads
document.addEventListener('DOMContentLoaded', () => {
    window.quizEngine = new AssemblyQuizEngine();
}); 